import angular from 'angular';
import accountSelectorTemplate from './accountSelector.template.html';
import './accountSelector.css';

export default angular
  .module('books.ui.accountSelector', ['ui.bootstrap'])
  .directive('accountSelector', accountSelectorDirective).name;

accountSelectorDirective.$inject = [];

/*****************************************************************************
 * Directive: accountSelector
 *
 * Utility for selecting an account. Provides two inputs such that a lookup can
 * be done by either the account number or the account name.
 *
 * @attr {object} accounts
 *   Array of accounts returned from the server call on the app.js
 *
 * @attr {string} modelIsAccount
 *   Indication the model to use will the be the whole account object. Defaults
 *   to false which means the accountID will be the model value.
 *
 * @attr {object} accountNumberHide
 *  Optional. Hides the account number input field when true.
 *
 * @attr {string} selectorLabel
 *   Label for the input field.
 *
 * @attr {object} labelHide
 *   Optional. Hides the label field when true.
 *
 * @attr {expression} labelColWidth
 *   Number of bootstrap grid columns the label should span. Defaults to 1.
 *****************************************************************************/
/* @ngInject */ function accountSelectorDirective() {
  return {
    restrict: 'E',
    template: accountSelectorTemplate,
    require: 'ngModel',
    scope: {
      accounts: '=',
      modelIsAccount: '@',
      accountNumberHide: '&',
      selectorLabel: '@',
      labelHide: '=',
      labelColWidth: '&'
    },
    link: function ($scope, element, attr, ngModel) {
      /** Inner account model. */ $scope.account = {};

      /*---------------------------------*/
      /* Add row class to element for    */
      /* grid layout of child components */
      /*---------------------------------*/
      element.addClass('row');

      /** Selected Account. */
      /*-----------------------------------*/
      /* overrides the focus function to   */
      /* set focus on the select element   */
      /*-----------------------------------*/
      element[0].focus = function () {
        element[0]
          .querySelector(
            $scope.accountNumberHide() ? 'input.as-name' : 'input.as-num'
          )
          .focus();
      };

      /*****************************************************************************
       * checkAccount */
      /**
       * Clears text that does not match a selection in the account input typeahead.
       * With typeahead if there is a string it becomes the model, however we want
       * to clear a string. This checks for an empty model or a string and clears the
       * inputs if either are true.
       *****************************************************************************/
      $scope.checkAccount = function () {
        if (!$scope.account || angular.isString($scope.account))
          $scope.account = null;
      };

      /** Label Column Width. */
      function colsLabel() {
        return $scope.noLabel() ? 0 : $scope.labelColWidth() || 1;
      }

      /** Name Column Width.  Takes up remainder of grid row. */
      function colsName() {
        return 12 - colsLabel() - colsNumber();
      }

      /** Number Column Width. Takes up a third of the space. */
      function colsNumber() {
        return $scope.accountNumberHide()
          ? 0
          : Math.round((12 - colsLabel()) / 3);
      }

      /*****************************************************************************
       * divNameClass */
      /**
       * Returns a string with bootstrap grid column classes that will be applied on
       * the account name div element.
       *
       * @return     String
       *****************************************************************************/
      $scope.divNameClass = function () {
        return 'col-sm-' + colsName() + ' col-xs-' + colsName();
      };

      /*****************************************************************************
       * divNumberClass */
      /**
       * Returns a string with bootstrap grid column classes that will be applied on
       * the account number div element.
       *
       * @return     String
       *****************************************************************************/
      $scope.divNumberClass = function () {
        return 'col-sm-' + colsNumber() + ' col-xs-' + colsNumber();
      };

      /*****************************************************************************
       * isInvalid */
      /**
       * Returns true if the directive element has the csmInvalid class on it. False
       * otherwise.
       *
       * @return     Boolean
       *****************************************************************************/
      $scope.isInvalid = function () {
        return element.hasClass('csmInvalid');
      };

      /*****************************************************************************
       * labelClass */
      /**
       * Returns a string with bootstrap grid column classes that will be applied on
       * the label.
       *
       * @return     String
       *****************************************************************************/
      $scope.labelClass = function () {
        return 'labelFormat col-sm-' + colsLabel() + ' col-xs-' + colsLabel();
      };

      /*****************************************************************************
       * noLabel */
      /**
       * Returns an indication that there is no label to be shown.
       *
       * @return     Boolean
       *****************************************************************************/
      $scope.noLabel = function () {
        return !$scope.selectorLabel || $scope.labelHide;
      };

      /*---------------------------------------------*/
      /* Set up a watcher for the disabled attribute */
      /*---------------------------------------------*/
      $scope.$watch(
        function () {
          return element[0].hasAttribute('disabled');
        },
        function (isDisabled) {
          $scope.isDisabled = isDisabled;
        }
      );

      /*--------------------------------------------------------------------*/
      /* Watches $scope.accounts from the controller, creates and sorts two */
      /* arrays for use in the account name and account number typeaheads.  */
      /*--------------------------------------------------------------------*/
      $scope.$watchCollection('accounts', function (list) {
        $scope.numbersList = (list || []).concat().sort(function (a, b) {
          var numA = a.accountNumber || '';
          var numB = b.accountNumber || '';

          return numA.localeCompare(numB);
        });
        $scope.namesList = (list || []).concat().sort(function (a, b) {
          return a.accountName.localeCompare(b.accountName);
        });
      });

      /*---------------------------------------*/
      /* Watches the NgModel for changes and   */
      /* submits the accountID in the request. */
      /*---------------------------------------*/
      $scope.$watch('account', function (account) {
        ngModel.$setViewValue(angular.isString(account) ? null : account);
      });

      /*------------------------------------------*/
      /* Return the accountID when modelIsAccount */
      /* is false, otherwise return the viewValue */
      /*------------------------------------------*/
      ngModel.$parsers.push(function (viewValue) {
        return viewValue && $scope.modelIsAccount != 'true'
          ? viewValue.accountID
          : viewValue;
      });

      /***********************************************************************
       * $render */
      /**
       * This will set the internal account model from the current list so the
       * inner inputs can update.
       ***********************************************************************/
      ngModel.$render = function () {
        $scope.account = ($scope.accounts || [])
          .filter(function (acct) {
            return (
              ngModel.$viewValue ==
              ($scope.modelIsAccount == 'true' ? acct : acct.accountID)
            );
          })
          .shift();
      };
    }
  };
}
